import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { styled } from '@linaria/react'
import { minimalVars } from '@ovasabi/ui-minimal/tokens'
import { MinimalAlert, MinimalButton, MinimalEmptyState } from '@ovasabi/ui-minimal'
import { DishRow, FilterPills, PageHeader, Screen, SearchField, type PillOption } from '../../components/chow'
import { ExploreState } from '../../components/chow/ExploreCard'
import { PlateIcon } from '../../components/icons'
import { type DishCategory } from './content'
import { ChowThumb } from './ChowThumb'
import { dishCardEta, useChefOpenMap, useKitchenNames, useUiDishes, type UiDish } from './projections'
import { useCartItemSnapshot, useChefMenuSnapshot } from '../../generated/prototypeRuntime'
import { useChooseChow } from './ChooseChowContext'
import { isLiveRuntime } from '../../lib/runtime'
import { activeCartRecords, addDishToCart } from './cart'
import { useAuth } from './auth/AuthContext'
import { recordPendingAction } from './auth/pendingAction'
import { useFavourites } from './favourites'
import { from } from '@ovasabi/ui-minimal/tokens'
import { enumShort } from './chefShared'
import { ExploreNavigation } from './ExploreNavigation'
import { usePublicMenus } from '../../lib/discover'
import { curatedMenus } from './content'
import { allowSeedContent } from '../../lib/runtime'

type FilterKey = 'all' | DishCategory

const options: readonly PillOption<FilterKey>[] = [
  { value: 'all', label: 'All Dishes' },
  { value: 'rice', label: 'Jollof & Rice' },
  { value: 'swallow', label: 'Swallow & Soups' },
  { value: 'grill', label: 'Suya & Grills' },
  { value: 'chops', label: 'Small Chops' },
  { value: 'drinks', label: 'Drinks & Coolers' },
  { value: 'breakfast', label: 'Breakfast' },
  { value: 'protein', label: 'Proteins' },
]

/* The count is a label on the grid, not a section of its own. Left as a direct
   child of the page body it collected a full body gap above AND below, so the
   line of small italic text floated in a band of empty ground twice its height
   between the filter pills and the first row of cards. Grouping the two puts
   one gap above the whole block and holds the label against what it counts. */
const Results = styled.div`
  display: grid;
  gap: ${minimalVars.space.md};
  min-width: 0;
`

const Count = styled.p`
  margin: 0;
  color: ${minimalVars.color.textTertiary};
  font-size: ${minimalVars.typography.captionSize};
`

const List = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${minimalVars.space.lg};
  ${from('hand')} { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  ${from('desk')} { grid-template-columns: repeat(3, minmax(0, 1fr)); }
`

const ClearChefFilter = styled.button`
  background: ${minimalVars.color.brandSoft};
  color: ${minimalVars.color.brand};
  border: 1px solid ${minimalVars.color.borderSubtle};
  padding: 8px 14px;
  border-radius: 10px;
  font-size: ${minimalVars.typography.captionSize};
  font-weight: ${minimalVars.typography.weightSemibold};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  box-sizing: border-box;
  transition: background-color 160ms ease, color 160ms ease;

  &:hover {
    background: ${minimalVars.color.brand};
    color: ${minimalVars.color.textInverse};
  }
`

const MenuShelf = styled.section`
  display: grid;
  gap: ${minimalVars.space.sm};
`

const MenuShelfHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${minimalVars.space.sm};
  flex-wrap: wrap;
`

const MenuShelfTitle = styled.h2`
  margin: 0;
  font-family: ${minimalVars.typography.displayFamily};
  font-size: 1.05rem;
  line-height: 1.25;
  color: ${minimalVars.color.textPrimary};
`

const MenuShelfLink = styled(Link)`
  color: ${minimalVars.color.brand};
  font-weight: 800;
  font-size: ${minimalVars.typography.captionSize};
  text-decoration: none;
  min-height: 44px;
  display: inline-flex;
  align-items: center;
`

const MenuRail = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${minimalVars.space.sm};

  ${from('hand')} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  ${from('desk')} {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`

const MenuCard = styled(Link)`
  display: grid;
  grid-template-columns: 76px minmax(0, 1fr);
  gap: ${minimalVars.space.sm};
  align-items: center;
  min-height: 118px;
  padding: ${minimalVars.space.sm};
  border: 1px solid ${minimalVars.color.borderSubtle};
  border-radius: ${minimalVars.radius.md};
  background: ${minimalVars.color.bgSurface};
  color: inherit;
  text-decoration: none;
  box-shadow: ${minimalVars.shadow.subtle};
  transition: border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease;

  &:focus-visible {
    outline: 2px solid ${minimalVars.color.borderFocus};
    outline-offset: 2px;
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      border-color: ${minimalVars.color.brand};
      box-shadow: ${minimalVars.shadow.medium};
      transform: translateY(-1px);
    }
  }
`

const MenuThumb = styled.div`
  width: 76px;
  height: 76px;
  border-radius: ${minimalVars.radius.sm};
  overflow: hidden;
  background: ${minimalVars.color.bgSurfaceAlt};

  > * {
    width: 100%;
    height: 100%;
  }
`

const MenuCopy = styled.div`
  display: grid;
  gap: 4px;
  min-width: 0;
`

const MenuTitle = styled.span`
  font-weight: 800;
  color: ${minimalVars.color.textPrimary};
  line-height: 1.25;
  overflow-wrap: anywhere;
`

const MenuMeta = styled.span`
  color: ${minimalVars.color.textSecondary};
  font-size: ${minimalVars.typography.captionSize};
  line-height: 1.35;
`

const getChefPossessive = (raw?: string): string => {
  if (!raw) return ''
  let name = raw.trim()
  if (name.includes('(')) {
    const main = name.replace(/\s*\([^)]*\)/g, '').trim()
    if (main) name = main
  }
  const stripped = name.replace(/\s+(Kitchen|Bakery|Grill|Store|Market|Restaurant|Patisserie)$/i, '').trim()
  if (stripped) name = stripped

  if (name.toLowerCase().endsWith("'s")) {
    return name
  }
  if (name.endsWith("'")) {
    return name
  }
  if (name.toLowerCase().endsWith('s') || name.toLowerCase().endsWith('z')) {
    return `${name}'`
  }
  return `${name}'s`
}

export const DishesPage = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const chefParam = searchParams.get('chef')
  const menuParam = searchParams.get('menu')
  const categoryParam = searchParams.get('category') || searchParams.get('filter')
  const initialFilter = (categoryParam && options.some((o) => o.value === categoryParam))
    ? (categoryParam as FilterKey)
    : 'all'
  const [filter, setFilter] = useState<FilterKey>(initialFilter)

  useEffect(() => {
    const raw = searchParams.get('category') || searchParams.get('filter')
    if (raw && options.some((o) => o.value === raw)) {
      setFilter(raw as FilterKey)
    } else if (!raw) {
      setFilter('all')
    }
  }, [searchParams])
  // Seeded from ?q= so a search started on the home header lands here already
  // applied. Typing after that is local state — the URL is the entry point, not
  // a live mirror of every keystroke.
  const [query, setQuery] = useState(() => searchParams.get('q') ?? '')
  const [qty, setQty] = useState<Record<string, number>>({})
  // Dish ids with an add-to-cart command in flight (live mode only).
  const [pendingIds, setPendingIds] = useState<Set<string>>(() => new Set())
  const { records: dishes, loading } = useUiDishes()
  const categoryOptions = useMemo(() => {
    const known = new Set(options.map((option) => option.value))
    const extra = [...new Set(dishes.map((dish) => dish.category))].filter((value) => !known.has(value))
    return [...options, ...extra.map((value) => ({ value, label: value.replace(/_/g, ' ').replace(/^./, (letter) => letter.toUpperCase()) }))]
  }, [dishes])
  const menus = useChefMenuSnapshot() as { records: readonly Record<string, unknown>[] }
  const { menus: liveMenus } = usePublicMenus()
  const kitchenName = useKitchenNames()

  const allMenus = useMemo(() => {
    const list: Record<string, unknown>[] = []
    if (liveMenus.length > 0) {
      for (const m of liveMenus) {
        list.push({
          ...m,
          status: 'published',
          cover_image_url: m.cover ?? '',
          chef_profile_id: m.chef_id,
        })
      }
    }
    for (const m of menus.records) {
      if (!list.some((r) => String(r.id) === String(m.id))) {
        list.push(m)
      }
    }
    if (allowSeedContent()) {
      for (const m of curatedMenus) {
        if (!list.some((r) => String(r.id) === String(m.id))) {
          list.push({ ...m })
        }
      }
    }
    return list
  }, [liveMenus, menus.records])

  const selectedMenu = menuParam ? allMenus.find((menu) => String(menu.id) === menuParam) : undefined
  const { status } = useAuth()
  const { profileId } = useChooseChow()
  const favourites = useFavourites(profileId)
  const cart = useCartItemSnapshot() as { records: readonly Record<string, unknown>[] }

  const cartItemRefs = useMemo(() => {
    const active = activeCartRecords(cart.records)
    const scoped = isLiveRuntime() ? active.filter((r) => String(r.customer_profile_id ?? '') === profileId) : active
    return new Set(scoped.map((item) => String(item.item_ref)))
  }, [cart.records, profileId])

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase()
    const cleanParam = chefParam ? chefParam.replace(/[^\w\s]/g, '').trim().toLowerCase() : ''
    return dishes.filter((dish) => {
      const matchesCategory =
        filter === 'all' ||
        dish.category === filter ||
        (filter === 'swallow' && dish.category === 'soups') ||
        (filter === 'grill' && dish.category === 'protein')
      const cleanChef = dish.chef.replace(/[^\w\s]/g, '').trim().toLowerCase()
      const matchesChef = !chefParam || cleanChef.includes(cleanParam) || cleanParam.includes(cleanChef)
      const matchesQuery =
        !q || dish.name.toLowerCase().includes(q) || dish.chef.toLowerCase().includes(q)
      return matchesCategory && matchesChef && matchesQuery && (!menuParam || dish.menuId === menuParam)
    })
  }, [filter, query, chefParam, menuParam, dishes])

  const namedMenus = useMemo(() => {
    const rows = allMenus
      .filter((menu) => enumShort(menu.status) === 'published')
      .map((menu) => {
        const items = dishes.filter((dish) => dish.menuId === String(menu.id))
        return { menu, items }
      })
      .filter(({ items }) => items.length > 0)
    return rows.slice(0, 3)
  }, [allMenus, dishes])

  // When browsing a single chef's menu, tell the customer if that kitchen is
  // closed right now — they can still add dishes and schedule at checkout.
  const openMap = useChefOpenMap()
  const filteredChefClosed = useMemo(() => {
    if (!chefParam || visible.length === 0) return false
    const chefId = visible[0].chefId
    return openMap.get(chefId) === false
  }, [chefParam, visible, openMap])

  const hasFilters = Boolean(query.trim() || chefParam || menuParam || filter !== 'all')
  const clearFilters = () => {
    setQuery('')
    setFilter('all')
    setSearchParams({})
  }

  // Effect to automatically clear pendingIds once an item successfully appears in the cart
  useEffect(() => {
    setPendingIds((prev) => {
      let changed = false
      const next = new Set(prev)
      for (const id of next) {
        const d = visible.find((v) => v.id === id)
        if (d && cartItemRefs.has(d.id)) {
          next.delete(id)
          changed = true
        }
      }
      return changed ? next : prev
    })
  }, [cartItemRefs, visible])

  const handleAdd = (dish: UiDish, quantity: number) => {
    if (status !== 'authenticated' || !profileId) {
      recordPendingAction({
        type: 'add_to_cart',
        dish: {
          id: dish.id,
          name: dish.name,
          price: dish.price,
          chefId: dish.chefId,
          chef: dish.chef,
          category: dish.category,
          image: dish.image,
          glyph: dish.glyph,
        },
        quantity,
        returnTo: '/dishes',
        timestamp: Date.now(),
      })
      navigate('/login?returnTo=' + encodeURIComponent('/dishes'))
      return
    }
    addDishToCart(dish, quantity, profileId)
  }

  const chefNamePossessive = chefParam ? getChefPossessive(chefParam) : ''
  const displayTitle = selectedMenu ? String(selectedMenu.title || 'Kitchen menu') : chefParam ? `${chefNamePossessive} dishes` : 'Dishes'

  return (
    <Screen
      header={
        <PageHeader title={displayTitle} accentDot>
          <SearchField placeholder="e.g. jollof, pasta, Amara…" value={query} onChange={setQuery} />
        </PageHeader>
      }
    >
      <ExploreNavigation />
      <FilterPills
        ariaLabel="Filter dishes"
        value={filter}
        options={categoryOptions}
        onChange={(val) => {
          setFilter(val)
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev)
            if (val === 'all') {
              next.delete('category')
              next.delete('filter')
            } else {
              next.set('category', val)
            }
            return next
          })
        }}
      />
      {menuParam && (
        <ClearChefFilter onClick={() => setSearchParams({})}>
          Viewing a chef’s menu <span>Show all dishes ×</span>
        </ClearChefFilter>
      )}
      {chefParam && (
        <ClearChefFilter onClick={() => setSearchParams({})}>
          <span>Viewing only {chefNamePossessive} dishes</span>
          <span>Clear ✕</span>
        </ClearChefFilter>
      )}
      {filteredChefClosed && (
        <MinimalAlert tone="info" title={`${chefNamePossessive} kitchen is closed right now`}>
          You can still add dishes and schedule your order for later at checkout.
        </MinimalAlert>
      )}
      {namedMenus.length > 0 && !menuParam && !chefParam && filter === 'all' && !query && (
        <MenuShelf aria-label="Chef menus">
          <MenuShelfHead>
            <MenuShelfTitle>Chef Menus</MenuShelfTitle>
            <MenuShelfLink to="/menus">See all menus →</MenuShelfLink>
          </MenuShelfHead>
          <MenuRail>
            {namedMenus.map(({ menu, items }) => {
              const id = String(menu.id)
              const title = String(menu.title || 'Kitchen menu')
              const chef =
                menu.chef_profile_id && kitchenName(String(menu.chef_profile_id)) !== 'Kitchen'
                  ? kitchenName(String(menu.chef_profile_id))
                  : String(menu.chef || kitchenName(String(menu.chef_profile_id || '')))
              return (
                <MenuCard key={id} to={`/dishes?menu=${encodeURIComponent(id)}`}>
                  <MenuThumb>
                    <ChowThumb
                      src={String(menu.cover_image_url || items[0]?.image || '')}
                      alt={title}
                      name={title}
                    />
                  </MenuThumb>
                  <MenuCopy>
                    <MenuTitle>{title}</MenuTitle>
                    <MenuMeta>{chef}</MenuMeta>
                    <MenuMeta>{items.length} {items.length === 1 ? 'dish' : 'dishes'} ready to browse</MenuMeta>
                  </MenuCopy>
                </MenuCard>
              )
            })}
          </MenuRail>
        </MenuShelf>
      )}
      <ExploreState loading={loading}>
      {visible.length === 0 ? (
        <MinimalEmptyState
          icon={<PlateIcon />}
          title={hasFilters ? 'No dishes match' : 'No dishes yet'}
          description={
            hasFilters
              ? 'Try a different search, chef, or category.'
              : 'Kitchens near you are still plating up their menus — check back soon.'
          }
          action={
            hasFilters ? (
              <MinimalButton variant="secondary" onClick={clearFilters}>
                Clear filters
              </MinimalButton>
            ) : undefined
          }
        />
      ) : (
        <Results>
          <Count role="status">{visible.length} {visible.length === 1 ? 'dish' : 'dishes'} to choose from</Count>
          <List>
            {visible.map((dish) => (
              <DishRow
                key={dish.id}
                name={dish.name}
                chef={dish.chef}
                chefGlyph={dish.chefGlyph}
                glyph={<ChowThumb src={dish.image} emoji={dish.glyph} alt={dish.name} name={dish.name} category={dish.category} />}
                price={dish.price}
                qty={qty[dish.id] || 1}
                onQty={(value) => setQty((prev) => ({ ...prev, [dish.id]: value }))}
                to={`/dishes/${encodeURIComponent(dish.id)}`}
                onFavourite={() => favourites.toggle(dish.id)}
                favourite={favourites.isFavourite(dish.id)}
                onAdd={() => {
                  handleAdd(dish, qty[dish.id] || 1)
                  setQty((prev) => ({ ...prev, [dish.id]: 1 }))
                }}
                addedToCart={cartItemRefs.has(dish.id)}
                pending={pendingIds.has(dish.id)}
                stock={dish.stock}
                status={dish.status}
                rating={dish.rating}
                etaLabel={dishCardEta(dish.cookTimeMinutes)}
              />
            ))}
          </List>
        </Results>
      )}
      </ExploreState>
    </Screen>
  )
}
